import Joi from "joi";
import { useEffect, useState } from "react";
import SweetAlert from "react-bootstrap-sweetalert";
import { connect } from "react-redux";
import {
  getColleges,
  getDepartmentAdmins,
  getDepartmentName,
} from "../../../../../action/common/commonActionCreator";
import { updateDepartment } from "../../../../../action/masters/departmentActionCreator";
import FloatingLabelDropDown from "../../../../../common-components/floatingdropdown";
import FloatingLabelTextInput from "../../../../../common-components/floatingtextinput";
import {
  baseURL,
  cloneDeep,
  maxImageSize,
} from "../../../../../constants/commonConstants";
import { validateFields } from "../../../../../helper/joi-validation";
import {
  getAlertData,
  alertConstants,
} from "../../../../../helper/sweet-alert";
import {
  IEditDepartment,
  IDepartmentData,
} from "../../../../../models/master/department";
import LoaderSpinner from "../../../../../common-components/loader";
import { UserRole } from "../../../../../constants/RoleEnum";
import { sortAlphabetically } from "../../../../../common-functions/commonFunctions";
import { areObjectsEqual } from "../../../../../helper/generic-helpers";

const DepartmentEdit = (props: IEditDepartment) => {
  const {
    updateDepartment,
    department,
    colleges,
    getColleges,
    setShowEdit,
    getAllDepartments,
    isCollegeAdmin,
    getDepartment,
    getLoginUsers,
    loginUsers,
    getDepartmentName,
    departmentName,
    role,
  } = props;

  // states

  const [data, setData] = useState<IDepartmentData>(
    cloneDeep(department || {})
  );
  const [initialData, setInitialData] = useState<IDepartmentData | undefined>(
    undefined
  );
  const [errors, setErrors] = useState<any>(undefined);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<any>("success");
  const [alertTitle, setAlertTitle] = useState<string>("Default");
  const [showAlertCancel, setShowAlertCancel] = useState<boolean>(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [name, setName] = useState<string>("");
  const [nameId, setNameId] = useState<number>(0);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [imageName, setImageName] = useState<string | null>(null);
  const [hasChange, setHasChange] = useState(false);

  // change the data at the time onchange fields
  const onChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    let _data = { ...data, [event.target.name]: event.target.value };
    if (event.target.name === "collegeId") {
      let _item =
        colleges &&
        colleges.find((college) => college.id === event.target.value);
      _data.collegeName = _item?.name;
      if (_data.collegeName) getDepartmentName(_data.collegeName);
    }
    if (event.target.name === "name") {
      let selectedDepartment = departmentName.find(
        (department) => department.id === event.target.value
      );
      setName(selectedDepartment?.name);
      setNameId(selectedDepartment?.id);
      _data.name = event.target.value;
      _data.collegeDepartmentName = `${_data.collegeName}-${selectedDepartment?.name}`;
    }

    errors && delete errors[event.target.name];
    setData(_data);
  };

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    errors && delete errors[event.target.name];
    if (event.target.files && event.target.files.length > 0) {
      const selectedFile = event.target.files[0];
      setImageName(selectedFile.name);
      const fileType = selectedFile.type;
      const fileSize = selectedFile.size;
      const validFileTypes = [
        "image/png",
        "image/jpeg",
        "image/jpg",
        "image/webp",
        "image/ico",
        "image/svg+xml",
      ];
      if (!validFileTypes.includes(fileType)) {
        setErrors({
          ...errors,
          file: "Invalid file type. Please upload an image.",
        });
      } else if (fileSize > maxImageSize) {
        setErrors({
          ...errors,
          file: "File size exceeds 1MB. Please upload a small file.",
        });
        setImagePreview(null);
        setImageName(null);
      } else {
        setData({ ...data, [event.target.name]: selectedFile });
        const fileURL = URL.createObjectURL(selectedFile);
        setImagePreview(fileURL);
      }
    }
  };

  useEffect(() => {
    if (department) {
      let _department = cloneDeep(department);
      setData(_department);
      setInitialData(_department);
      getDepartmentName(_department.collegeName);
      if (_department.departmentLogo) {
        const imageUrl = baseURL + _department.departmentLogo;
        setImagePreview(imageUrl);
        setImageName(imageUrl);
      }
      setNameId(department?.externalId);
      setName(department?.name);
    }
  }, [department]);

  // setting sweet alert dynamically
  const setAlert = (type: string) => {
    let alertData = getAlertData(type);
    if (alertData) {
      setShowAlert(true);
      setAlertType(alertData.type);
      setAlertTitle(alertData.title);
    }
  };

  // executed on clicking 'OK' in sweet alert.
  const onConfirm = () => {
    if (alertType === alertConstants.error) {
      setShowAlert(false);
    } else {
      if (isCollegeAdmin) {
        getDepartment && getDepartment();
        setShowEdit(false);
      } else {
        getAllDepartments && getAllDepartments();
        setShowEdit(false);
      }
    }
  };

  const schema = Joi.object().keys({
    name: Joi.alternatives().try(
      Joi.string().required().label("Select Department"),
      Joi.number().greater(0).label("Select Department")
    ),
    collegeId: Joi.number().greater(0).required().label("Select College"),
    file: Joi.any().when("isFile", {
      is: true,
      then: Joi.any().optional(),
      otherwise: Joi.any().required().label("File is required"),
    }),
    description: Joi.string().required().label("Description is required"),
    location: Joi.string().required().label("Location is required"),
    phoneNumber: Joi.string()
      .length(10)
      .required()
      .label("Phone number must be 10 digits"),
    address: Joi.string().required().label("Address is required"),
    email: Joi.string()
      .regex(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)
      .required()
      .label("Enter valid email"),
  });

  const validationData = {
    ...data,
    isFile: Boolean(data.file),
  };

  const onUpdate = async () => {
    const validationErrors = validateFields(validationData, schema);
    if (validationErrors === undefined) {
      if (data.departmentLoginUserId && loginUsers) {
        const selectedUser = loginUsers.find(
          (user: any) => user.id === data.departmentLoginUserId
        );
        if (selectedUser) {
          data.departmentLoginUserName = selectedUser.name;
        }
      }

      const formData = new FormData();
      formData.append("id", data.id?.toString() || "");
      formData.append("name", name);
      formData.append("externalId", nameId.toString());
      formData.append("file", data.file);
      formData.append("description", data.description.replace(/\s+$/g, ""));
      formData.append("collegeDepartmentName", data.collegeDepartmentName);
      formData.append("address", data.address);
      formData.append("location", data.location);
      formData.append("phoneNumber", data.phoneNumber);
      formData.append("email", data.email);
      formData.append("collegeId", data.collegeId.toString() || "");
      formData.append(
        "departmentLoginUserId",
        data.departmentLoginUserId != null
          ? data.departmentLoginUserId.toString()
          : ""
      );
      formData.append(
        "departmentLoginUserName",
        data.departmentLoginUserName || ""
      );
      formData.append("isActive", "true");

      setLoading(true);
      setIsDisabled(true);
      try {
        await updateDepartment(formData, data.id ?? 0, setAlert);
      } finally {
        setLoading(false);
        setIsDisabled(false);
      }
    } else {
      setErrors(validationErrors);
    }
  };

  useEffect(() => {
    if (data && initialData) {
      setHasChange(!areObjectsEqual(data, initialData));
    }
  }, [data, initialData]);

  useEffect(() => {
    getColleges();
    getLoginUsers();
  }, []);

  return (
    <>
      {data && (
        <>
          <div className="modal-header">
            <h1 className="modal-title fs-5" id="addnewbuttonsLabel">
              Update Department
            </h1>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
              onClick={() => setShowEdit(false)}
            ></button>
          </div>
          <div className="modal-body">
            <div className="row">
              <div className="col-md-6 form-group">
                <FloatingLabelDropDown
                  label={"University Management"}
                  onChange={onChange}
                  isMandatory={true}
                  value={data.collegeId}
                  name={"collegeId"}
                  optionsList={sortAlphabetically(colleges)}
                  disable={role === UserRole.DepartmentAdmin}
                />
                {errors?.collegeId && (
                  <div className="text-danger">{errors.collegeId}</div>
                )}
              </div>
              <div className="col-md-6 form-group">
                <FloatingLabelDropDown
                  label={"Department Name "}
                  onChange={onChange}
                  isMandatory={true}
                  value={nameId}
                  name={"name"}
                  optionsList={sortAlphabetically(departmentName)}
                  disable={role === UserRole.DepartmentAdmin}
                />
                {errors?.name && (
                  <div className="text-danger">{errors.name}</div>
                )}
              </div>
              <div className="col-md-6 form-group">
                <FloatingLabelTextInput
                  label={"Address "}
                  onChange={onChange}
                  isMandatory={true}
                  value={data.address}
                  name={"address"}
                  maxLength={50}
                  disable={role === UserRole.DepartmentAdmin}
                />
                {errors?.address && (
                  <div className="text-danger">{errors.address}</div>
                )}
              </div>
              <div className="col-md-6 form-group">
                <FloatingLabelTextInput
                  label={"Location "}
                  onChange={onChange}
                  isMandatory={true}
                  value={data.location}
                  name={"location"}
                  maxLength={50}
                  disable={role === UserRole.DepartmentAdmin}
                />
                {errors?.location && (
                  <div className="text-danger">{errors.location}</div>
                )}
              </div>
              <div className="col-md-6 form-group">
                <FloatingLabelTextInput
                  label={"Phone Number "}
                  onChange={onChange}
                  isMandatory={true}
                  value={data.phoneNumber}
                  name={"phoneNumber"}
                  maxLength={10}
                />
                {errors?.phoneNumber && (
                  <div className="text-danger">{errors.phoneNumber}</div>
                )}
              </div>
              <div className="col-md-6 form-group">
                <FloatingLabelTextInput
                  label={"Email "}
                  onChange={onChange}
                  isMandatory={true}
                  value={data.email}
                  name={"email"}
                  maxLength={50}
                />
                {errors?.email && (
                  <div className="text-danger">{errors.email}</div>
                )}
              </div>
              <div className="col-md-6 form-group">
                <label className="profile_details_text">
                  Department Logo<i className="label-asterisk"> * </i>
                </label>
                <div className="inputfile-flex">
                  {imagePreview && (
                    <div className="inputfile-img">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        style={{ width: "50px", height: "50px" }}
                      />
                    </div>
                  )}
                  <div className="inputfile-wrap">
                    <input
                      type="file"
                      name="file"
                      id="image"
                      className="form-control d-none"
                      required
                      onChange={onFileChange}
                      accept="image/png, image/jpeg, image/jpg, image/svg+xml, image/webp, image/x-icon, text/html"
                    />
                    <div className="inputfile">
                      <label htmlFor="image">Choose File</label>
                      <span>
                        {imageName
                          ? imageName.trim().split("/").pop()
                          : "No File Choosen"}
                      </span>
                    </div>
                  </div>
                </div>
                {errors?.file && (
                  <div className="text-danger">{errors.file}</div>
                )}
              </div>
              <div className="col-md-6 form-group">
                <FloatingLabelDropDown
                  label={"Login user ID "}
                  onChange={onChange}
                  isMandatory={false}
                  value={data.departmentLoginUserId}
                  name={"departmentLoginUserId"}
                  optionsList={sortAlphabetically(loginUsers)}
                  disable={role === UserRole.DepartmentAdmin}
                />
                {errors?.departmentLoginUserId && (
                  <div className="text-danger">
                    {errors.departmentLoginUserId}
                  </div>
                )}
              </div>
              <div className="col-md-12 form-group">
                <label className="profile_details_text">
                  Description<i className="label-asterisk"> * </i>
                </label>
                <textarea
                  name="description"
                  value={data.description}
                  onChange={onChange}
                  maxLength={1000}
                ></textarea>
                {errors?.description && (
                  <div className="text-danger">{errors.description}</div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-primary"
                onClick={onUpdate}
                disabled={isDisabled || !hasChange}
              >
                Update
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
                onClick={() => setShowEdit(false)}
              >
                Close
              </button>
            </div>
          </div>
          <SweetAlert
            show={showAlert}
            success={false}
            type={alertType}
            title={alertTitle}
            onConfirm={() => onConfirm()}
            showCancel={showAlertCancel}
            onCancel={() => {
              setShowAlert(false);
              setShowAlertCancel(false);
            }}
            cancelBtnText={"No"}
            confirmBtnText={showAlertCancel ? "Yes" : "Ok"}
          ></SweetAlert>
          {loading && <LoaderSpinner />}
        </>
      )}
    </>
  );
};

const mapDispatchToProps = {
  updateDepartment: (data: any, id: number, callBack: Function) =>
    updateDepartment(data, id, callBack),
  getColleges: () => getColleges(),
  getLoginUsers: () => getDepartmentAdmins(),
  getDepartmentName: (college: string) => getDepartmentName(college),
};

function mapStateToProps(state: any) {
  return {
    colleges: state.common.colleges,
    loginUsers: state.common.loginUsers,
    departmentName: state.common.departmentName,
    role: state.userDetails.userProfileData?.currentRole,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(DepartmentEdit);
